import { useState, useRef, useCallback } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import * as THREE from "three";
import { Canvas } from "@react-three/fiber";
import { View } from "@react-three/drei";

import ModelView from "./ModelView";
import { models, sizes } from "../constants";
import { animateWithGsapTimeline } from "../utils/animations";
import { yellowImg } from "../utils";

const Model = () => {
  const [size, setSize] = useState("small");
  const [model, setModel] = useState({
    title: "iPhone 15 Pro in Natural Titanium",
    color: ["#8F8A81", "#FFE7B9", "#6F6C64"],
    img: yellowImg,
  });

  // Kamera kontrolleri
  const cameraControlSmall = useRef(null);
  const cameraControlLarge = useRef(null);

  // 3D Model grupları
  const small = useRef(new THREE.Group());
  const large = useRef(new THREE.Group());

  // Performans Optimizasyonu: Sürekli render'ı önlemek için State yerine Ref kullanıyoruz.
  // 3D model döndürülürken React bileşeninin saniyede 60 kez render edilmesini engeller.
  const smallRotation = useRef(0);
  const largeRotation = useRef(0);

  // GSAP Timeline'ı her render'da yeniden oluşturmamak için referans olarak tutuyoruz.
  const tl = useRef(gsap.timeline());

  // Sadece ilk yüklemede başlık animasyonu
  useGSAP(() => {
    gsap.to("#heading", {
      y: 0,
      opacity: 1,
      duration: 1,
      ease: "power2.out",
    });
  }, []);

  // Boyut değiştiğinde tetiklenecek model geçiş animasyonları
  useGSAP(() => {
    // Çakışmaları önlemek için timeline'ı temizliyoruz
    tl.current.clear();

    if (size === "large") {
      animateWithGsapTimeline(
        tl.current,
        small,
        smallRotation.current,
        "#view1",
        "#view2",
        { transform: "translateX(-100%)", duration: 2, ease: "power2.inOut" },
      );
    } else if (size === "small") {
      animateWithGsapTimeline(
        tl.current,
        large,
        largeRotation.current,
        "#view2",
        "#view1",
        { transform: "translateX(0)", duration: 2, ease: "power2.inOut" },
      );
    }
  }, [size]);

  // Gereksiz render'ları önlemek için Event Handler'ları memoize ediyoruz
  const handleModelChange = useCallback((item) => {
    setModel(item);
  }, []);

  const handleSizeChange = useCallback((value) => {
    setSize(value);
  }, []);

  return (
    <section className="common-padding">
      <div className="screen-max-width">
        <h1 id="heading" className="section-heading opacity-0 translate-y-5">
          Take a closer look.
        </h1>

        <div className="flex flex-col items-center mt-5">
          <div className="w-full h-[75vh] md:h-[90vh] overflow-hidden relative">
            <ModelView
              index={1}
              groupRef={small}
              gsapType="view1"
              controlRef={cameraControlSmall}
              setRotationState={(val) => (smallRotation.current = val)}
              item={model}
              size={size}
            />

            <ModelView
              index={2}
              groupRef={large}
              gsapType="view2"
              controlRef={cameraControlLarge}
              setRotationState={(val) => (largeRotation.current = val)}
              item={model}
              size={size}
            />

            <Canvas
              className="w-full h-full pointer-events-none"
              style={{
                position: "fixed",
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                overflow: "hidden",
              }}
              // Next.js veya SSR (Sunucu Taraflı Render) kaynaklı hydration hatalarını önler
              eventSource={
                typeof document !== "undefined"
                  ? document.getElementById("root")
                  : undefined
              }
            >
              <View.Port />
            </Canvas>
          </div>

          <div className="mx-auto w-full mt-10">
            <p className="text-sm font-light text-center mb-5">{model.title}</p>

            <div className="flex-center">
              {/* Renk Seçici */}
              <ul className="color-container flex items-center justify-center">
                {models.map((item, i) => (
                  <li key={i} className="mx-2">
                    <button
                      type="button"
                      aria-label={`Select color ${item.title}`}
                      className={`w-6 h-6 rounded-full cursor-pointer transition-all duration-300 hover:scale-110 ${
                        model.title === item.title
                          ? "ring-2 ring-white ring-offset-2 ring-offset-black"
                          : ""
                      }`}
                      style={{ backgroundColor: item.color[0] }}
                      onClick={() => handleModelChange(item)}
                    />
                  </li>
                ))}
              </ul>

              {/* Boyut Seçici */}
              <div className="size-btn-container ml-5 flex items-center bg-gray-900 rounded-full p-1">
                {sizes.map(({ label, value }) => (
                  <button
                    key={label}
                    type="button"
                    className="size-btn transition-colors duration-300 px-4 py-1 rounded-full text-sm font-medium"
                    style={{
                      backgroundColor: size === value ? "white" : "transparent",
                      color: size === value ? "black" : "white",
                    }}
                    onClick={() => handleSizeChange(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Model;
